// gzip for outbound request bodies, in two shapes:
//
//   gzipBlob     — a Blob, for a presigned S3 PUT. storage-hub and
//                  conciliatron's solid-repair tool both sign
//                  `Content-Encoding: gzip` into the URL and must send the
//                  exact same encoding, or S3 rejects the PUT with
//                  SignatureDoesNotMatch.
//   gzipJsonBody — a JSON value plus the two headers that describe it, for
//                  every API client in the app.
//
// Both live here so the encoding is spelled once. Nothing else in the repo may
// write the literal 'gzip' into a header: that is how solidRepairs.ts and
// files.ts came to disagree about the same wire contract.

export const GZIP_ENCODING = 'gzip';

/** The Content-Type a gzipped JSON body still declares: the encoding describes
 * how the bytes are wrapped, not what they are. */
export const JSON_CONTENT_TYPE = 'application/json';

/** A body ready to hand to fetch, with the headers that describe it. */
export interface EncodedJsonBody {
  body: BodyInit;
  /** Content-Type always; Content-Encoding only when the body is compressed. */
  headers: Record<string, string>;
}

/**
 * Serialise `value` as JSON and gzip it, returning the bytes together with the
 * headers that describe them.
 *
 * Every API client calls this instead of formatting `Content-Type` and
 * `Content-Encoding` itself — one implementation, so no client can compress
 * without advertising it (a 400 from the backend) or advertise without
 * compressing (also a 400).
 *
 * Compression is unconditional, not size-gated. A threshold would mean small
 * bodies take a different path from large ones, and that is precisely the
 * failure shape this exists to remove: the 2026-09-09 upload incident was
 * invisible until a payload got big. Uniformity makes a broken preflight or an
 * unmounted server middleware fail on the first request instead of on the first
 * wide CSV. gzip costs ~20 bytes of framing on a tiny body, which is the price.
 *
 * It does NOT gzip when CompressionStream is unavailable — an older WebView, or
 * a non-secure context — and returns the plain JSON string with only
 * Content-Type. The server middleware keys off Content-Encoding, so an
 * uncompressed body is read normally: the app degrades in bandwidth, never in
 * function. gzipBlob has no such fallback because a presigned PUT has the
 * encoding signed into its URL and cannot degrade.
 *
 * `value` must not be undefined — JSON.stringify yields undefined for it, which
 * is not a body. Callers already branch on whether a request has one.
 */
export async function gzipJsonBody(value: unknown): Promise<EncodedJsonBody> {
  const json = JSON.stringify(value);

  if (typeof CompressionStream === 'undefined') {
    return { body: json, headers: { 'Content-Type': JSON_CONTENT_TYPE } };
  }

  // Encode to bytes and feed a one-chunk stream, rather than going through a
  // Blob: it keeps this independent of Blob.prototype.stream, which jsdom does
  // not implement — so a consumer's tests need no Blob shim to exercise the real
  // compression path.
  const source = new ReadableStream<Uint8Array>({
    start(controller) {
      controller.enqueue(new TextEncoder().encode(json));
      controller.close();
    },
  });

  // Same cast as gzipBlob: the DOM lib types CompressionStream's writable side
  // as BufferSource, which doesn't unify with a Uint8Array stream.
  const gzip = new CompressionStream(GZIP_ENCODING) as unknown as ReadableWritablePair<
    Uint8Array,
    Uint8Array
  >;

  return {
    body: await new Response(source.pipeThrough(gzip)).arrayBuffer(),
    headers: { 'Content-Type': JSON_CONTENT_TYPE, 'Content-Encoding': GZIP_ENCODING },
  };
}

/** Gzip a Blob via native CompressionStream — streams, so a multi-GB file
 * is never held in memory whole. There is no counterpart gunzip exported
 * here: S3 echoes the stored Content-Encoding, so browsers and Go's
 * net/http both decode for free on a plain HTTP download. A consumer that
 * instead fetches the object via an SDK client (bypassing HTTP transport
 * negotiation) must decompress explicitly, server-side. */
export async function gzipBlob(source: Blob): Promise<Blob> {
  // The DOM lib types CompressionStream's writable side as BufferSource, which
  // doesn't unify with a Uint8Array stream. Uint8Array IS a BufferSource, so
  // restate the pair type rather than widening the chain.
  const gzip = new CompressionStream(GZIP_ENCODING) as unknown as ReadableWritablePair<
    Uint8Array,
    Uint8Array
  >;
  return await new Response(source.stream().pipeThrough(gzip)).blob();
}

/**
 * Read a JSON body produced by gzipJsonBody — the inverse, kept here so the
 * round-trip is defined in exactly one place.
 *
 * Its consumers are test doubles standing in for the backend: MSW handlers and
 * fetch stubs get the raw bytes and, unlike a real server (and unlike
 * httpauth.GunzipRequest), decode no Content-Encoding of their own. Without this
 * each of them grows its own inflate, which is the duplication the single
 * gzipJsonBody exists to prevent.
 *
 * Tolerates an uncompressed body, so a caller need not know whether
 * gzipJsonBody took its no-CompressionStream fallback.
 *
 * `Request` only, deliberately — never a `Response`. A compressed response is
 * already decoded by the time JS can touch it (the browser does it in its
 * network stack, as does Go's net/http), while Content-Encoding may still be
 * visible on the headers. Accepting one here would invite a double-inflate on a
 * body that was never compressed any more.
 */
export async function readJsonBody<T>(source: Request): Promise<T> {
  if (source.headers.get('Content-Encoding') !== GZIP_ENCODING) {
    return (await source.json()) as T;
  }
  const compressed = source.body;
  if (!compressed) throw new Error('readJsonBody: request declared gzip but carried no body');
  const plain = compressed.pipeThrough(new DecompressionStream(GZIP_ENCODING));
  return JSON.parse(await new Response(plain).text()) as T;
}
